import {useEffect, useRef, useState} from "react";
import style from "../../catalogs/popup/createCatalog.module.scss";
import popupStyle from "./productPopup.module.scss";
import {API_URL} from "../../../http";

const emptyProduct = {title: "", description: "", cost: "", weight: ""};

export function ProductPopup({active, setActive, create, update}) {
    const [product, setProduct] = useState(emptyProduct);
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const isEditing = Boolean(active.data?.id);
    const existingImage = active.data?.images?.[0]?.src || active.data?.image;
    const existingImageUrl = existingImage
        ? `${API_URL}${existingImage.startsWith("/") ? existingImage : `/${existingImage}`}`
        : null;

    useEffect(() => {
        setProduct({
            title: active.data?.title || "",
            description: active.data?.description || "",
            cost: active.data?.cost ?? "",
            weight: active.data?.weight ?? ""
        });
        setImageFile(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [
        active.visible,
        active.data?.id,
        active.data?.title,
        active.data?.description,
        active.data?.cost,
        active.data?.weight
    ]);

    const close = () => {
        setActive({visible: false, categoryId: active.categoryId, data: {}});
        setProduct(emptyProduct);
        setImageFile(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            e.target.value = "";
            alert("Пожалуйста, выберите изображение.");
            return;
        }

        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setImageFile(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!product.title.trim() || product.cost === "") return;

        const formData = new FormData();
        formData.append("title", product.title.trim());
        formData.append("description", product.description.trim());
        formData.append("cost", product.cost);
        formData.append("categoryId", active.categoryId);
        if (product.weight !== "") {
            formData.append("weight", product.weight);
        }
        if (imageFile) {
            formData.append("file", imageFile);
        }

        if (isEditing) {
            formData.append("id", active.data.id);
            update(formData);
        } else {
            create(formData);
        }

        close();
    };

    return (
        <div className={active.visible ? [style.block, style.blockActive].join(" ") : style.block}>
            <div className={style.title}>{isEditing ? "Редактирование товара" : "Создание товара"}</div>
            <button className={style.back} onClick={close}>Назад</button>
            <form onSubmit={handleSubmit}>
                <div className={style.label}>Название товара</div>
                <input
                    className={style.input}
                    type="text"
                    placeholder="Название товара"
                    value={product.title}
                    onChange={(e) => setProduct({...product, title: e.target.value})}
                    required
                />
                <div className={style.label}>Описание</div>
                <textarea
                    className={style.input}
                    placeholder="Описание товара"
                    value={product.description}
                    onChange={(e) => setProduct({...product, description: e.target.value})}
                    rows={4}
                />
                <div className={style.label}>Цена</div>
                <input
                    className={style.input}
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Например, 500"
                    value={product.cost}
                    onChange={(e) => setProduct({...product, cost: e.target.value})}
                    required
                />
                <div className={style.label}>Вес (необязательно)</div>
                <input
                    className={style.input}
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Например, 300"
                    value={product.weight}
                    onChange={(e) => setProduct({...product, weight: e.target.value})}
                />
                <div className={style.photo}>
                    <div>
                        <div className={style.label}>Фото товара</div>
                        <div className={style.description}>
                            Выберите изображение товара для загрузки.
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            className={style.hiddenInput}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className={style.uploadButton}
                        >
                            <span className={style.plus}>＋</span> Загрузить фото
                        </button>
                    </div>
                    <div>
                        {preview ? (
                            <div className={style.previewContainer}>
                                <div className={popupStyle.productPreviewFrame}>
                                    <img src={preview} alt="Предпросмотр" className={popupStyle.productPreviewImage}/>
                                </div>
                                <button type="button" className={style.deleteImage} onClick={removeImage}>
                                    <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16.8037 6.19531L6.19711 16.8019" stroke="#CE1919" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M16.8037 16.8027L6.19711 6.19613" stroke="#CE1919" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </button>
                            </div>
                        ) : existingImageUrl ? (
                            <div className={style.previewContainer}>
                                <div className={popupStyle.productPreviewFrame}>
                                    <img
                                        src={existingImageUrl}
                                        alt="Фото товара"
                                        className={popupStyle.productPreviewImage}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                <button className={style.btnSave} type="submit">
                    {isEditing ? "Сохранить товар" : "Создать товар"}
                </button>
            </form>
        </div>
    );
}
